<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CommercialImportsAPI from 'dashboard/api/commercialImports';
import { useAlert } from 'dashboard/composables';

defineProps({ show: { type: Boolean, default: false } });
const emit = defineEmits(['close', 'imported']);
const { t } = useI18n();

const file = ref(null);
const preview = ref(null);
const report = ref(null);
const isLoading = ref(false);
const dryRun = ref(true);
const mapping = reactive({});
const reportStats = computed(() =>
  report.value
    ? [
        [t('COMMERCIAL.IMPORT.CREATED'), report.value.created],
        [t('COMMERCIAL.IMPORT.UPDATED'), report.value.updated],
        [t('COMMERCIAL.IMPORT.SKIPPED'), report.value.skipped],
        [t('COMMERCIAL.IMPORT.ERRORS'), report.value.errors.length],
      ]
    : []
);

const fields = computed(() => [
  ['company_nit', t('COMMERCIAL.IMPORT.FIELDS.COMPANY_NIT')],
  ['company_name', t('COMMERCIAL.IMPORT.FIELDS.COMPANY_NAME')],
  ['company_domain', t('COMMERCIAL.IMPORT.FIELDS.COMPANY_DOMAIN')],
  ['contact_name', t('COMMERCIAL.IMPORT.FIELDS.CONTACT_NAME')],
  ['contact_email', t('COMMERCIAL.IMPORT.FIELDS.CONTACT_EMAIL')],
  ['contact_phone', t('COMMERCIAL.IMPORT.FIELDS.CONTACT_PHONE')],
  ['deal_name', t('COMMERCIAL.IMPORT.FIELDS.DEAL_NAME')],
  ['description', t('COMMERCIAL.IMPORT.FIELDS.DESCRIPTION')],
  ['source', t('COMMERCIAL.IMPORT.FIELDS.SOURCE')],
  ['business_line', t('COMMERCIAL.IMPORT.FIELDS.BUSINESS_LINE')],
  ['amount', t('COMMERCIAL.IMPORT.FIELDS.AMOUNT')],
  ['currency', t('COMMERCIAL.IMPORT.FIELDS.CURRENCY')],
  ['probability', t('COMMERCIAL.IMPORT.FIELDS.PROBABILITY')],
  ['expected_close_date', t('COMMERCIAL.IMPORT.FIELDS.CLOSE_DATE')],
  ['stage', t('COMMERCIAL.IMPORT.FIELDS.STAGE')],
  ['loss_reason', t('COMMERCIAL.IMPORT.FIELDS.LOSS_REASON')],
  ['advisor', t('COMMERCIAL.IMPORT.FIELDS.ADVISOR')],
]);

const normalize = value =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '_');

const autoMap = headers => {
  fields.value.forEach(([field]) => {
    const normalizedField = normalize(field);
    mapping[field] =
      headers.find(header => normalize(header) === normalizedField) || '';
  });
};

const handleFile = event => {
  [file.value] = event.target.files;
  preview.value = null;
  report.value = null;
};

const loadPreview = async () => {
  if (!file.value) return;
  isLoading.value = true;
  try {
    const { data } = await CommercialImportsAPI.preview(file.value);
    preview.value = data.payload;
    autoMap(preview.value.headers);
  } catch {
    useAlert(t('COMMERCIAL.IMPORT.MESSAGES.PREVIEW_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const runImport = async () => {
  isLoading.value = true;
  try {
    const { data } = await CommercialImportsAPI.importData({
      blobId: preview.value.blobId,
      mapping,
      dryRun: dryRun.value,
    });
    report.value = data.payload;
    if (!dryRun.value) emit('imported');
  } catch {
    useAlert(t('COMMERCIAL.IMPORT.MESSAGES.IMPORT_ERROR'));
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div>
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="emit('close')"
    >
      <section
        class="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl border border-n-weak bg-n-background p-5 shadow-xl"
      >
        <header class="flex items-start justify-between gap-3">
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('COMMERCIAL.IMPORT.TITLE') }}
            </h2>
            <p class="text-sm text-n-slate-10">
              {{ t('COMMERCIAL.IMPORT.DESCRIPTION') }}
            </p>
          </div>
          <button type="button" class="text-n-slate-10" @click="emit('close')">
            <span class="i-lucide-x size-5" />
          </button>
        </header>

        <div class="mt-5 flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".csv,.xlsx"
            class="text-sm text-n-slate-11"
            @change="handleFile"
          />
          <button
            type="button"
            class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            :disabled="!file || isLoading"
            @click="loadPreview"
          >
            {{ t('COMMERCIAL.IMPORT.PREVIEW') }}
          </button>
        </div>

        <template v-if="preview">
          <p class="mt-4 text-sm text-n-slate-10">
            {{
              t('COMMERCIAL.IMPORT.ROWS_FOUND', { count: preview.totalRows })
            }}
          </p>
          <div class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label
              v-for="field in fields"
              :key="field[0]"
              class="text-xs text-n-slate-11"
            >
              {{ field[1] }}
              <select
                v-model="mapping[field[0]]"
                class="mt-1 block h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm"
              >
                <option value="">
                  {{ t('COMMERCIAL.IMPORT.DO_NOT_IMPORT') }}
                </option>
                <option
                  v-for="header in preview.headers"
                  :key="header"
                  :value="header"
                >
                  {{ header }}
                </option>
              </select>
            </label>
          </div>

          <div class="mt-5 overflow-x-auto rounded-lg border border-n-weak">
            <table class="min-w-full text-left text-xs">
              <thead class="bg-n-alpha-2 text-n-slate-11">
                <tr>
                  <th
                    v-for="header in preview.headers"
                    :key="header"
                    class="whitespace-nowrap px-3 py-2"
                  >
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak text-n-slate-12">
                <tr v-for="(row, index) in preview.rows" :key="index">
                  <td
                    v-for="header in preview.headers"
                    :key="header"
                    class="whitespace-nowrap px-3 py-2"
                  >
                    {{ row[header] }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mt-5 flex flex-wrap items-center justify-between gap-3">
            <label class="flex items-center gap-2 text-sm text-n-slate-11">
              <input v-model="dryRun" type="checkbox" />
              {{ t('COMMERCIAL.IMPORT.DRY_RUN') }}
            </label>
            <button
              type="button"
              class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              :disabled="isLoading || !mapping.deal_name"
              @click="runImport"
            >
              {{
                dryRun
                  ? t('COMMERCIAL.IMPORT.VALIDATE')
                  : t('COMMERCIAL.IMPORT.IMPORT')
              }}
            </button>
          </div>
        </template>

        <section
          v-if="report"
          class="mt-5 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
        >
          <h3 class="font-medium text-n-slate-12">
            {{ t('COMMERCIAL.IMPORT.REPORT') }}
          </h3>
          <div class="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <p v-for="stat in reportStats" :key="stat[0]">
              <span class="block text-xs text-n-slate-9">{{ stat[0] }}</span>
              <strong>{{ stat[1] }}</strong>
            </p>
          </div>
          <ul
            v-if="report.errors.length"
            class="mt-3 space-y-1 text-xs text-n-ruby-11"
          >
            <li
              v-for="error in report.errors"
              :key="`${error.row}-${error.message}`"
            >
              {{
                t('COMMERCIAL.IMPORT.ROW_ERROR', {
                  row: error.row,
                  message: error.message,
                })
              }}
            </li>
          </ul>
        </section>
      </section>
    </div>
  </div>
</template>
